import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PostEditor } from "@/components/admin/PostEditor";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminGenerateDraft } from "@/lib/blog-admin.functions";

export const Route = createFileRoute("/admin/posts/new")({
  component: NewPost,
  head: () => ({ meta: [{ title: "New post · Admin" }, { name: "robots", content: "noindex" }] }),
});

const CATEGORIES = [
  "DSCR Basics",
  "Eligibility & Qualification",
  "Costs & Fees",
  "Cash-Out & HELOC",
  "Jumbo Reverse",
  "Retirement Planning",
  "Heirs & Estate",
  "Refinance & Payoff",
  "Compliance & Consumer Protection",
  "State & Local",
];

function NewPost() {
  const gen = useServerFn(adminGenerateDraft);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [topic, setTopic] = useState("");
  const [category, setCategory] = useState("DSCR Basics");
  const [notes, setNotes] = useState("");
  const [withHero, setWithHero] = useState(true);
  const [busy, setBusy] = useState(false);

  const draft = async () => {
    if (topic.trim().length < 6) return toast.error("Enter a real topic (min 6 chars)");
    setBusy(true);
    try {
      const r = (await gen({
        data: { topic, category, notes: notes || null, generateHero: withHero },
      })) as any;
      await qc.invalidateQueries({ queryKey: ["admin-posts"] });
      toast.success("Draft generated");
      if (r?.post?.id) void navigate({ to: "/admin/posts/$id", params: { id: r.post.id } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Draft failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <BlogSubNav />
      <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-5 w-5 text-primary" />
          <div className="flex-1">
            <h2 className="font-display text-lg font-bold">AI draft a new post</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Generates a 1,600–2,200 word SEO article (title, meta, body, FAQ, internal links + optional hero image) as a draft
              for your review. 100% original, factual, compliant with our advertising rules.
            </p>
            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_240px]">
              <div>
                <Label htmlFor="ai-topic">Topic / keyword</Label>
                <Input
                  id="ai-topic"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. DSCR cash-out refinance on a Florida rental 2026"
                />
              </div>
              <div>
                <Label htmlFor="ai-cat">Category</Label>
                <select
                  id="ai-cat"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-3">
              <Label htmlFor="ai-notes">Notes / angle (optional)</Label>
              <Textarea
                id="ai-notes"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Angle, audience, specific must-haves (e.g. compare vs HELOC, include a decision table for ages 62/70/80)"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={withHero} onCheckedChange={setWithHero} />
                Auto-generate hero image
              </label>
              <Button disabled={busy} onClick={draft}>
                {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1 h-4 w-4" />}
                Draft with AI
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">Or write manually</div>
      <PostEditor />
    </div>
  );
}
